import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Colour is reserved for the states that ask for an action.
 *
 * `ok` used to be success-green, and green is a CLAIM: it says "this is good".
 * "Closes in sixty days" is not good, it is merely not yet urgent — so in any
 * real list, where most things are far away, the majority of rings carried a
 * semantic colour they had not earned and the one urgent ring had to compete
 * with them instead of winning outright. Neutral is the honest reading.
 *
 * `ok` and `past` therefore share an ink, and that is fine: the ARC already
 * separates them. A distant deadline is a nearly full ring, a closed one has
 * no arc at all. That is the division of labour a ring exists for — geometry
 * carries how much time is left, colour carries whether you must act.
 */
const TONE_COLOR = {
    urgent: 'var(--color-danger, var(--color-error, #dc2626))',
    soon: 'var(--color-warning, #b45309)',
    ok: 'var(--color-text-muted, var(--text-muted, #9ca3af))',
    past: 'var(--color-text-muted, var(--text-muted, #9ca3af))',
};
export function countdownTone(days) {
    if (days == null)
        return 'past';
    if (days < 0)
        return 'past';
    if (days <= 7)
        return 'urgent';
    if (days <= 30)
        return 'soon';
    return 'ok';
}
/** "3d" fits; "today" is the case that actually matters and must not read "0d". */
export function countdownLabel(days) {
    if (days == null)
        return '—';
    if (days < 0)
        return 'closed';
    if (days === 0)
        return 'today';
    return `${days}d`;
}
export const CountdownRing = ({ days, window: windowDays = 90, size = 38, thickness = 4, tone, label, className, }) => {
    const resolved = tone || countdownTone(days);
    const color = TONE_COLOR[resolved];
    const r = (size - thickness) / 2;
    const c = 2 * Math.PI * r;
    // Remaining share of the window. Clamped at both ends: a close date beyond
    // the window is a full ring, not an overflowing one, and a closed item keeps
    // a visible track rather than vanishing.
    const remaining = days == null || days < 0
        ? 0
        : Math.max(0, Math.min(1, days / windowDays));
    // A gap of exactly zero still deserves a tick of arc, or "closes today"
    // renders as an empty ring indistinguishable from "already closed".
    const arc = days != null && days >= 0 ? Math.max(remaining * c, 2) : 0;
    return (_jsxs("svg", { width: size, height: size, viewBox: `0 0 ${size} ${size}`, className: `${className || ''} chsvg chsvg--fixed`, role: label ? 'img' : 'presentation', "aria-label": label, "aria-hidden": label ? undefined : true, children: [_jsx("circle", { cx: size / 2, cy: size / 2, r: r, fill: "none", stroke: "var(--color-border, var(--border, #e5e7eb))", strokeWidth: thickness }), arc > 0 && (_jsx("circle", { cx: size / 2, cy: size / 2, r: r, fill: "none", stroke: color, strokeWidth: thickness, strokeDasharray: `${arc} ${c}`, strokeLinecap: "round", transform: `rotate(-90 ${size / 2} ${size / 2})` })), _jsx("text", { x: size / 2, y: size / 2, textAnchor: "middle", dominantBaseline: "central", fill: color, fontSize: days != null && days >= 0 && days < 100 ? 11 : 9, fontWeight: 600, className: "chnum", children: countdownLabel(days) })] }));
};
export default CountdownRing;
